import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Track whether a horizontally-clipping element currently overflows its own
 * visible width (its content is wider than its box, i.e. something is clipped).
 *
 * The observed element must clip horizontally (`overflow: hidden` / `min-width:
 * 0` so it can shrink below its content) for `scrollWidth`/`clientWidth` to
 * differ. Re-measures on element resize via `ResizeObserver` — so it reacts to a
 * table column being drag-resized wider (the "drag to reveal clipped content"
 * gesture) as well as viewport changes.
 *
 * Used to conditionally show a right-edge "there is more" fade cue ONLY when the
 * content is actually clipped, never fading a cell whose content fits. Mirrors
 * the inline overflow-detection pattern in `StateBadges.tsx`, factored out so the
 * inline action-button rows (RecordTable actions cells, bundle list) can reuse it.
 *
 * @param deps optional values that, when changed, trigger a re-measure (e.g. the
 *   number of rendered items — a content change that need not change the box size
 *   and so would not by itself fire the ResizeObserver).
 */
export function useHorizontalOverflow<T extends HTMLElement>(deps: unknown[] = []) {
    const ref = useRef<T>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        setIsOverflowing(el.scrollWidth - el.clientWidth > 1);
    }, []);

    useEffect(() => {
        measure();
        const el = ref.current;
        if (!el || typeof ResizeObserver === "undefined") {
            return;
        }
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measure, ...deps]);

    return { ref, isOverflowing };
}
